// src/hooks/useVehiculeFetcher.tsx
/* Hook encargado de hacer la consulta a la base y
traer el vehiculo a partir de su id

Autor: Benjamín León
Fecha: 2024-08-16
*/
import { useState } from 'react'

import Vehicle from '../interfaces/Vehicle.ts'
import vehicleTypeTranslate from '../utils/vehicleTypeTranslate.ts'
import { fechaIsoToYYYY_MM_DD, formatCurrency, formatWithThousandSep } from '../utils/utils'
import fuelTranslate from '../utils/fuelTranslate'
import stateTranslate from '../utils/stateTranslate'
import gearboxTranslate from '../utils/gearboxTranslate.ts'
import colorTranslate from '../utils/colorTranslate'
import { customFetch } from '../utils/customFetch.ts'
import VehicleBase from '../interfaces/VehicleBase.ts'

import { useAuth } from './useAuth.tsx'
import { makeUrlVehicle, makeUrlVehiclesByPlate } from './connectionData'

const useVehicleFetcher = () => {
  const [errorVehicle, setErrorVehicle] = useState<Error | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const { userData } = useAuth()

  const fetchVehiclesByPlate = async (plate: string) => {
    setLoading(true)

    try {
      const data = await fetchDatabaseByPlate(plate)
      setErrorVehicle(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorVehicle(err)
      } else {
        setErrorVehicle(new Error('An unknown error occurred'))
      }

      return []
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicle = async (id: string) => {
    setLoading(true)

    try {
      const data = await fetchDatabase(id)
      setErrorVehicle(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorVehicle(err)
      } else {
        setErrorVehicle(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoading(false)
    }
  }
  const fetchVehicleBase = async (id: string) => {
    setLoading(true)

    try {
      const apiUrl = makeUrlVehicle(id)
      const data = await customFetch<VehicleBase>(apiUrl)
      setErrorVehicle(null)

      return data
    } catch (err) {
      // Assert the error as an instance of Error
      if (err instanceof Error) {
        setErrorVehicle(err)
      } else {
        setErrorVehicle(new Error('An unknown error occurred'))
      }

      return null
    } finally {
      setLoading(false)
    }
  }
  const fetchDatabase = async (id: string): Promise<Vehicle> => {
    const vehicle: Vehicle = await customFetch<Vehicle>(makeUrlVehicle(id))
    formatVehicle(vehicle)

    vehicle.gearbox_format = gearboxTranslate[vehicle.gearbox] || vehicle.gearbox
    vehicle.color_format = colorTranslate[vehicle.color] || vehicle.color

    vehicle.fuel_format = fuelTranslate[vehicle.fuel] || vehicle.fuel
    vehicle.state_format = stateTranslate[vehicle.state] || vehicle.state
    vehicle.appraisal_format = formatCurrency(vehicle.appraisal)
    vehicle.price_format = formatCurrency(vehicle.price)

    vehicle.suggestedPrice_format = formatCurrency(vehicle.suggestedPrice)
    vehicle.priceConsignment_format = formatCurrency(vehicle.priceConsignment ?? 0)
    vehicle.pricePurchased_format = formatCurrency(vehicle.pricePurchased ?? 0)

    return vehicle
  }
  const fetchDatabaseByPlate = async (plate: string): Promise<Vehicle[]> => {
    const apiUrl = makeUrlVehiclesByPlate(userData?.organization?._id ?? '', plate)

    // Fetching documents from the collection and converting them into an array
    //const response = await fetch(apiUrl);
    const vehicle = await customFetch<Vehicle[]>(apiUrl)

    vehicle.forEach((veh) => {
      formatVehicle(veh)
    })

    return vehicle
  }

  // A continuacion, algunos formateos que vamos a necesitar
  // OJO: Nos pisamos checkup con un "Date" de verdad
  const formatVehicle = (vehicle: Vehicle) => {
    if (vehicle.documents) {
      vehicle.documents.checkup = fechaIsoToYYYY_MM_DD(vehicle.documents.checkup)
    } else {
      vehicle.documents = { checkup: '', insurance: '', permit: '' } as any
    }

    vehicle.type_format = vehicleTypeTranslate[vehicle.type] || vehicle.type
    vehicle.odometer_format = formatWithThousandSep(vehicle.odometer)
    vehicle.fuel_format = fuelTranslate[vehicle.fuel] || vehicle.fuel
    vehicle.state_format = stateTranslate[vehicle.state] || vehicle.state
    vehicle.appraisal_format = formatCurrency(vehicle.appraisal)
    vehicle.price_format = formatCurrency(vehicle.price)

    vehicle.suggestedPrice_format = formatCurrency(vehicle.suggestedPrice)
  }

  // Exponemos estas variables al exterior
  return { errorVehicle, loading, fetchVehicle, fetchVehicleBase, fetchVehiclesByPlate }
}

export default useVehicleFetcher
